import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from "react-router-dom";
import { LogoMark } from "@evyely/ui";
import { ArrowLeft, BookOpen, Briefcase, Building2, ExternalLink, FileText, HelpCircle, Mail, Megaphone, Newspaper, Scale, Shield, Sparkles, Users, } from "lucide-react";
const SALES_EMAIL = "mailto:sales@evyely.com";
const SUPPORT_EMAIL = "mailto:support@evyely.com";
const SECURITY_EMAIL = "mailto:security@evyely.com";
const LEGAL_EMAIL = "mailto:legal@evyely.com";
export const STATIC_PAGE_ROUTES = [
    "enterprise",
    "changelog",
    "about",
    "careers",
    "blog",
    "contact",
    "docs",
    "help",
    "community",
    "api",
    "privacy",
    "terms",
    "security",
    "dpa",
    "delete-account",
];
export const STATIC_PAGE_CONTENT = {
    enterprise: {
        id: "enterprise",
        eyebrow: "Product",
        title: "Enterprise",
        intro: "Evyely for teams that need private, reliable AI support across interviews, sales calls, customer research, and internal meetings.",
        icon: _jsx(Building2, { size: 22 }),
        sections: [
            {
                title: "Built for teams",
                body: "Enterprise workspaces can support shared knowledge, admin controls, team billing, onboarding support, and security review.",
                bullets: [
                    "Workspace-level account management",
                    "Shared templates and knowledge bases",
                    "Security questionnaires and vendor review",
                    "Custom onboarding for recruiting, sales, and operations teams",
                ],
                links: [{ label: "Contact sales", href: SALES_EMAIL }],
            },
            {
                title: "Flexible deployment",
                body: "Tell us how your team works today and we will help map Evyely into the tools and meeting flows you already use.",
            },
        ],
    },
    changelog: {
        id: "changelog",
        eyebrow: "Product",
        title: "Changelog",
        intro: "Recent product updates, desktop releases, and platform improvements from the Evyely team.",
        icon: _jsx(Megaphone, { size: 22 }),
        sections: [
            {
                title: "June 2026",
                body: "Desktop reliability and deployment improvements for the macOS app, production backend connection, and authentication flow.",
                bullets: [
                    "Desktop auth now uses the correct app-specific Clerk configuration",
                    "macOS package rebuilt and verified against the production API health endpoint",
                    "Download page points users to current desktop installers",
                ],
            },
            {
                title: "Coming next",
                body: "More release notes will appear here as the web app, desktop app, and billing flow continue to ship.",
            },
        ],
    },
    about: {
        id: "about",
        eyebrow: "Company",
        title: "About Evyely",
        intro: "Evyely is building a real-time AI copilot that helps people communicate clearly when the conversation matters most.",
        icon: _jsx(Sparkles, { size: 22 }),
        sections: [
            {
                title: "Our mission",
                body: "We believe the best AI should make you sound more like yourself, not less. Evyely listens, understands context, and offers private guidance while you stay in control.",
            },
            {
                title: "Where we focus",
                body: "Interviews, meetings, sales calls, customer conversations, and any moment where preparation and presence need to meet in real time.",
            },
        ],
    },
    careers: {
        id: "careers",
        eyebrow: "Company",
        title: "Careers",
        intro: "We are a small, focused team building thoughtful AI tools for live conversations.",
        icon: _jsx(Briefcase, { size: 22 }),
        sections: [
            {
                title: "Open roles",
                body: "We do not have public openings listed yet, but we are always glad to hear from builders who care about product quality, privacy, and practical AI.",
                links: [{ label: "Introduce yourself", href: "mailto:careers@evyely.com" }],
            },
            {
                title: "How we work",
                body: "Remote-first, product-minded, and direct. We value people who can move from ambiguity to shipped work with care.",
            },
        ],
    },
    blog: {
        id: "blog",
        eyebrow: "Company",
        title: "Blog",
        intro: "Product notes, hiring advice, meeting workflows, and lessons from building real-time AI assistance.",
        icon: _jsx(Newspaper, { size: 22 }),
        sections: [
            {
                title: "Publishing soon",
                body: "The blog is being prepared. For now, the best places to start are the product walkthrough, pricing page, and desktop download page.",
                links: [
                    { label: "View pricing", href: "/pricing" },
                    { label: "Download desktop app", href: "/download" },
                ],
            },
        ],
    },
    contact: {
        id: "contact",
        eyebrow: "Company",
        title: "Contact",
        intro: "Reach the right Evyely inbox for support, sales, partnerships, security, or legal questions.",
        icon: _jsx(Mail, { size: 22 }),
        sections: [
            {
                title: "Get in touch",
                body: "Send us a note and include the account email, platform, and a short description when your message is about support.",
                links: [
                    { label: "Support", href: SUPPORT_EMAIL },
                    { label: "Sales", href: SALES_EMAIL },
                    { label: "Security", href: SECURITY_EMAIL },
                    { label: "Legal", href: LEGAL_EMAIL },
                ],
            },
        ],
    },
    docs: {
        id: "docs",
        eyebrow: "Resources",
        title: "Docs",
        intro: "Quick guidance for installing Evyely, signing in, connecting to the backend, and using the desktop app.",
        icon: _jsx(BookOpen, { size: 22 }),
        sections: [
            {
                title: "Getting started",
                body: "Create an account, choose a plan, install the desktop app, then sign in from the local desktop window.",
                bullets: [
                    "Use the Download page for macOS and Windows installers",
                    "Use the same email for web billing and desktop access",
                    "If desktop auth fails, quit and reopen the app before retrying sign-in",
                ],
                links: [{ label: "Download app", href: "/download" }],
            },
            {
                title: "Using Evyely",
                body: "Start a session before your meeting, keep the overlay available, and use uploaded context to make live suggestions more specific.",
            },
        ],
    },
    help: {
        id: "help",
        eyebrow: "Resources",
        title: "Help",
        intro: "Answers for account access, desktop setup, billing, and meeting workflow issues.",
        icon: _jsx(HelpCircle, { size: 22 }),
        sections: [
            {
                title: "Support checklist",
                body: "Most setup issues are caused by an old desktop build, a stale sign-in session, or a local browser permission prompt.",
                bullets: [
                    "Install the latest desktop app",
                    "Sign out and sign in again",
                    "Confirm microphone and screen permissions on macOS or Windows",
                    "Check that your network can reach api.evyely.com",
                ],
                links: [{ label: "Email support", href: SUPPORT_EMAIL }],
            },
        ],
    },
    community: {
        id: "community",
        eyebrow: "Resources",
        title: "Community",
        intro: "A place for Evyely users to share interview workflows, meeting templates, and product feedback.",
        icon: _jsx(Users, { size: 22 }),
        sections: [
            {
                title: "Coming soon",
                body: "The public community is not open yet. Until then, send feedback directly and we will route it to the product team.",
                links: [{ label: "Send feedback", href: "mailto:feedback@evyely.com" }],
            },
        ],
    },
    api: {
        id: "api",
        eyebrow: "Resources",
        title: "API",
        intro: "Evyely API access is planned for teams that want to connect conversation intelligence to internal workflows.",
        icon: _jsx(FileText, { size: 22 }),
        sections: [
            {
                title: "Developer access",
                body: "Public API keys are not generally available yet. Enterprise customers can request early access for approved integrations.",
                bullets: [
                    "Session metadata export",
                    "Meeting summary handoff",
                    "Knowledge base sync",
                    "Workspace analytics",
                ],
                links: [{ label: "Request API access", href: SALES_EMAIL }],
            },
        ],
    },
    privacy: {
        id: "privacy",
        eyebrow: "Legal",
        title: "Privacy",
        intro: "A plain-language overview of how Evyely approaches personal data, conversation context, and account information.",
        icon: _jsx(Shield, { size: 22 }),
        sections: [
            {
                title: "What we collect",
                body: "Evyely uses account details, billing status, product settings, and conversation context needed to provide the service.",
            },
            {
                title: "How we use data",
                body: "We use data to authenticate users, operate the product, process billing, improve reliability, and respond to support requests.",
            },
            {
                title: "Questions",
                body: "For privacy requests, account deletion, or data questions, contact our legal inbox.",
                links: [
                    { label: "Contact legal", href: LEGAL_EMAIL },
                    { label: "Delete your account", href: "/delete-account" },
                ],
            },
        ],
    },
    terms: {
        id: "terms",
        eyebrow: "Legal",
        title: "Terms",
        intro: "Core terms for using Evyely, including account responsibility, acceptable use, subscriptions, and product availability.",
        icon: _jsx(Scale, { size: 22 }),
        sections: [
            {
                title: "Using Evyely",
                body: "Use Evyely lawfully, keep account credentials secure, and do not use the service to capture or process information without the required consent.",
            },
            {
                title: "Subscriptions",
                body: "Paid plans renew based on the selected billing interval unless cancelled before renewal. Prices and features can change with notice.",
            },
            {
                title: "Contact",
                body: "For contractual questions or enterprise terms, contact legal or sales.",
                links: [
                    { label: "Contact legal", href: LEGAL_EMAIL },
                    { label: "Contact sales", href: SALES_EMAIL },
                ],
            },
        ],
    },
    security: {
        id: "security",
        eyebrow: "Legal",
        title: "Security",
        intro: "How Evyely thinks about protecting accounts, desktop sessions, conversation content, and production systems.",
        icon: _jsx(Shield, { size: 22 }),
        sections: [
            {
                title: "Security practices",
                body: "Evyely uses encrypted transport, authenticated API access, separated backend services, and provider-level controls for sensitive infrastructure.",
                bullets: [
                    "HTTPS and WSS for production traffic",
                    "Authenticated access for protected APIs",
                    "Desktop app connects to the production backend over api.evyely.com",
                    "Security reports can be sent directly to security@evyely.com",
                ],
                links: [{ label: "Report a security issue", href: SECURITY_EMAIL }],
            },
        ],
    },
    dpa: {
        id: "dpa",
        eyebrow: "Legal",
        title: "DPA",
        intro: "Enterprise customers can request a data processing addendum for vendor review and procurement workflows.",
        icon: _jsx(FileText, { size: 22 }),
        sections: [
            {
                title: "Request a DPA",
                body: "Send your company name, billing owner, and any procurement requirements. We will follow up with the current DPA process.",
                links: [{ label: "Contact legal", href: LEGAL_EMAIL }],
            },
        ],
    },
    // Google Play requires a publicly reachable account-deletion page that works
    // without installing the app. Linked from the Play listing's Data safety form.
    "delete-account": {
        id: "delete-account",
        eyebrow: "Legal",
        title: "Delete your account",
        intro: "How to permanently delete your Evyely account and the data associated with it, from the app or by request.",
        icon: _jsx(Shield, { size: 22 }),
        sections: [
            {
                title: "Delete from the mobile app",
                body: "Open Evyely on iOS or Android, go to Settings, and tap Delete account. Confirm the prompt. Deletion is immediate and cannot be undone.",
            },
            {
                title: "Request deletion by email",
                body: "If you cannot access the app, email us from the address on your account and we will delete it within 30 days.",
                links: [{ label: "Request deletion", href: SUPPORT_EMAIL }],
            },
            {
                title: "What gets deleted",
                body: "Your account and sign-in credentials, meeting and interview sessions, transcripts, notes, knowledge base entries, and uploaded files are permanently removed.",
                bullets: [
                    "Account and authentication records",
                    "Sessions, transcripts, and generated notes",
                    "Knowledge base entries and uploaded documents",
                    "Saved settings and prompt profiles",
                ],
            },
            {
                title: "What we retain",
                body: "Billing and invoice records are retained where required by tax and accounting law. They are not used to re-identify you in the product.",
            },
        ],
    },
};
function PageAction({ link }) {
    const isInternal = link.href.startsWith("/");
    const isExternal = link.href.startsWith("http");
    if (isInternal) {
        return (_jsx(Link, { to: link.href, className: "inline-flex items-center justify-center rounded-sm bg-[#9EE8D4] px-5 py-2.5 text-[13px] font-medium text-[#04151A]", children: link.label }));
    }
    return (_jsxs("a", { href: link.href, target: isExternal ? "_blank" : undefined, rel: isExternal ? "noreferrer" : undefined, className: "inline-flex items-center justify-center gap-2 rounded-sm bg-[#9EE8D4] px-5 py-2.5 text-[13px] font-medium text-[#04151A]", children: [link.label, isExternal ? _jsx(ExternalLink, { size: 13 }) : null] }));
}
export default function StaticPage({ pageId }) {
    const page = STATIC_PAGE_CONTENT[pageId];
    return (_jsxs("main", { className: "min-h-screen bg-[#04151A] text-[#E7F6F1]", children: [_jsx("nav", { className: "border-b border-[#9EE8D4]/15 px-6 py-5 md:px-10", children: _jsxs("div", { className: "mx-auto flex max-w-6xl items-center justify-between gap-4", children: [_jsxs(Link, { to: "/", className: "flex items-center gap-2.5 text-[17px] font-semibold text-[#E7F6F1]", children: [_jsx(LogoMark, { size: 26, variant: "tide" }), "Evyely"] }), _jsxs(Link, { to: "/", className: "inline-flex items-center gap-2 border border-[#9EE8D4]/30 px-4 py-2 text-[13px] text-[#9EE8D4]", children: [_jsx(ArrowLeft, { size: 14 }), "Home"] })] }) }), _jsx("section", { className: "px-6 py-16 md:px-10 md:py-24", children: _jsxs("div", { className: "mx-auto max-w-6xl", children: [_jsxs("div", { className: "max-w-3xl", children: [_jsx("div", { className: "inline-flex h-11 w-11 items-center justify-center border border-[#9EE8D4]/30 text-[#9EE8D4]", children: page.icon }), _jsx("p", { className: "mt-6 text-[10px] font-medium uppercase tracking-[0.2em] text-[#9EE8D4]", children: page.eyebrow }), _jsx("h1", { className: "mt-3 text-[#E7F6F1]", style: {
                                        fontSize: "clamp(38px, 5vw, 68px)",
                                        lineHeight: 1.05,
                                    }, children: page.title }), _jsx("p", { className: "mt-6 max-w-2xl text-[16px] leading-relaxed text-[#9EE8D4] md:text-[18px]", children: page.intro })] }), _jsx("div", { className: "mt-12 grid gap-4 md:grid-cols-2", children: page.sections.map((section) => (_jsxs("article", { className: "border border-[#9EE8D4]/20 p-6 md:p-7", children: [_jsx("h2", { className: "text-[21px] font-medium text-[#E7F6F1]", children: section.title }), _jsx("p", { className: "mt-3 text-[14px] leading-relaxed text-[#9EE8D4]", children: section.body }), section.bullets ? (_jsx("ul", { className: "mt-5 space-y-3", children: section.bullets.map((bullet) => (_jsxs("li", { className: "flex gap-3 text-[13px] leading-relaxed text-[#9EE8D4]", children: [_jsx("span", { className: "mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#9EE8D4]" }), _jsx("span", { children: bullet })] }, bullet))) })) : null, section.links ? (_jsx("div", { className: "mt-6 flex flex-wrap gap-3", children: section.links.map((link) => (_jsx(PageAction, { link: link }, link.label))) })) : null] }, section.title))) })] }) })] }));
}
